/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Collapse, Form, Flex, Row, Col, Space, Card, message } from "antd";
import { Input, Button, Table, Typography, DatePicker } from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { MdOutlineLibraryAdd } from "react-icons/md";
import { accessColumn } from "./model";
import useColumnSettings from "../../../hooks/useColumnSettings";
import useAccessPageState from "../../../hooks/useAccessPageState";
import dayjs from "dayjs";
import InvoiceService from "../../../service/ERP/Invoice.service";
import "./MyPage.css";

const invService = InvoiceService();
const { RangePicker } = DatePicker;

const mngConfig = { title: "", action: "create", code: null };

const InvoiceAccess = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [accessData, setAccessData] = useState([]);
  const [activeSearch, setActiveSearch] = useState([]);
  const {
    tablePagination,
    saveState,
    restoreState,
    clearState,
    restoreDefaultPagination,
    handleTableChange,
  } = useAccessPageState({ form });

  const handleSearch = (forcedValues = null, paginationOverride = null) => {
    const runSearch = (v) => {
      saveState(v, paginationOverride ?? tablePagination);
      const data = { ...v };
      if (data?.inv_date) {
        const [inv_date_form, inv_date_to] = data.inv_date.map((m) => dayjs(m).format("YYYY-MM-DD"));
        Object.assign(data, { inv_date_form, inv_date_to });
        delete data.inv_date;
      }
      invService
        .search(data, { ignoreLoading: Object.keys(data).length !== 0 })
        .then((res) => setAccessData(res.data?.data || []))
        .catch(() => message.error("Request error!"));
    };

    if (useAccessPageState.canUseForcedSearchValues?.(forcedValues)) {
      runSearch(forcedValues);
      return;
    }
    form.validateFields().then((v) => runSearch(v));
  };

  const handleClear = () => {
    clearState();
    form.resetFields();
    handleSearch({}, restoreDefaultPagination());
  };

  const handleAdd = () => {
    navigate("manage/create", {
      state: { config: { ...mngConfig, title: "Create Invoice", action: "create" } },
    });
  };

  const handleView = (record) => {
    navigate("manage/view", {
      state: { config: { ...mngConfig, title: "View Invoice", action: "view", code: record?.inv_code } },
    });
  };

  const handleEdit = (record) => {
    navigate("manage/edit", {
      state: { config: { ...mngConfig, title: "Edit Invoice", action: "edit", code: record?.inv_code } },
    });
  };

  const handleDelete = async (record) => {
    try {
      await invService.deleted(record?.inv_code);
      message.success("ลบสำเร็จ");
      handleSearch();
    } catch {
      message.error("ลบไม่สำเร็จ");
    }
  };

  const handlePrint = (record) => {
    window.open(`/inv-print/${record?.inv_code}`, "_blank");
  };

  useEffect(() => {
    const restored = restoreState();
    handleSearch(restored?.searchValues ?? null, restored?.tablePagination);
  }, []);

  const FormSearch = (
    <Collapse
      size="small"
      bordered={false}
      activeKey={activeSearch}
      onChange={setActiveSearch}
      items={[
        {
          key: "1",
          label: <><SearchOutlined /><span> ค้นหา</span></>,
          showArrow: false,
          children: (
            <>
              <br />
              <Form form={form} layout="vertical" autoComplete="off">
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="เลขที่ Invoice" name="inv_code">
                      <Input placeholder="Enter Invoice Code" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Job Order" name="jocode">
                      <Input placeholder="Enter JO Code" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="ชื่อลูกค้า" name="cusname">
                      <Input placeholder="Enter Customer Name" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Brand" name="brand">
                      <Input placeholder="Enter Brand" onChange={handleSearch} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="วันที่ Invoice" name="inv_date">
                      <RangePicker
                        placeholder={["From Date", "To Date"]}
                        style={{ width: "100%", height: 40 }}
                        onChange={handleSearch}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={12} />
                  <Col xs={24} sm={12}>
                    <Flex justify="flex-end" gap={8}>
                      <Button type="primary" size="small" className="bn-action" icon={<SearchOutlined />} onClick={() => handleSearch()}>ค้นหา</Button>
                      <Button type="primary" size="small" className="bn-action" danger icon={<ClearOutlined />} onClick={handleClear}>ล้าง</Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </>
          ),
        },
      ]}
    />
  );

  const allColumns = accessColumn({ handleView, handleEdit, handleDelete, handlePrint });

  /* คอลัมน์ที่แสดง ผู้ใช้เลือกเองได้ ระบบจำไว้ใน cookie ของเครื่องนี้ */

  const { columns: column, settingsButton } = useColumnSettings(

    "invoice_access_cols",

    allColumns

  );

  const TitleTable = (
    <Flex className="width-100" align="center">
      <Col span={12} className="p-0">
        <Typography.Title className="m-0 !text-zinc-800" level={3}>Invoice</Typography.Title>
      </Col>
      <Col span={12} style={{ paddingInline: 0 }}>
        <Flex gap={8} justify="end">
          {settingsButton}
          <Button
            size="small"
            className="bn-action bn-center bn-primary-outline justify-center"
            icon={<MdOutlineLibraryAdd style={{ fontSize: ".9rem" }} />}
            onClick={handleAdd}
          >
            Create Invoice
          </Button>
        </Flex>
      </Col>
    </Flex>
  );

  return (
    <div className="invoice-access">
      <Space direction="vertical" size="middle" style={{ display: "flex", position: "relative" }}>
        {FormSearch}
        <Card>
          <Row gutter={[8, 8]} className="m-0">
            <Col xs={24}>
              <Table
                title={() => TitleTable}
                size="small"
                rowKey="inv_code"
                columns={column}
                dataSource={accessData}
                scroll={{ x: "max-content" }}
                pagination={tablePagination}
                onChange={handleTableChange}
              />
            </Col>
          </Row>
        </Card>
      </Space>
    </div>
  );
};

export default InvoiceAccess;
